import { useEffect, useState } from 'react';
import type { CcyCode, TelegramListeningChat, TelegramMessageLogEntry, TelegramRule } from '@wave/shared';
import { fmtAmt } from '@wave/shared';
import { hasRight, useAuthStore } from '../auth/store';
import {
  useAccounts,
  useTelegramBotConfig,
  useTelegramBotStatus,
  useTelegramChatBalances,
  useTelegramChats,
  useTelegramHandlers,
  useTelegramMessages,
  useTelegramRules,
} from '../api/hooks';
import { Combobox } from '../components/Combobox';
import {
  useDeleteTelegramChat,
  useDeleteTelegramRule,
  useSaveBotConfig,
  useTestBotToken,
  useUpdateTelegramChat,
  useUpsertTelegramChat,
} from '../api/mutations';
import { TelegramRuleEditorModal } from '../components/modals/TelegramRuleEditorModal';
import { TelegramTemplatePicker } from '../components/modals/TelegramTemplatePicker';
import {
  btnDanger,
  btnGhostSm,
  btnPrimary,
  btnPrimarySm,
  cardL1,
  fieldLabelCls,
  inputCls,
  pageContainer,
  pageHeader,
  pageSubtitle,
  pageTitle,
  subSectionTitle,
  tableCell,
  tableCls,
  tableHead,
  tableHeadCell,
  tableRow,
  tableWrapper,
} from '../lib/styles';

type Tab = 'bot' | 'chats' | 'rules' | 'messages';

export function AdminTelegramPage() {
  const rights = useAuthStore((s) => s.rights);
  // telegram.manage — полный доступ (бот/правила/чаты); telegram.manage-chats —
  // только чаты (роли operator/manager). Вкладки «Бот»/«Правила» — лишь под manage.
  const canManage = hasRight(rights, 'telegram.manage');
  const canChats = canManage || hasRight(rights, 'telegram.manage-chats');
  const [tab, setTab] = useState<Tab>(canManage ? 'bot' : 'chats');

  return (
    <div className={pageContainer}>
      <div className={pageHeader}>
        <div>
          <h1 className={pageTitle}>Telegram-listener</h1>
          <p className={pageSubtitle}>
            Бот слушает указанные чаты, ищет совпадения по regex-правилам и создаёт{' '}
            <strong>pending события</strong> на странице{' '}
            <a href="/admin/telegram/events" className="text-accent hover:underline">
              События
            </a>
            . Применяет оператор вручную.
          </p>
        </div>
      </div>

      <div className="border-b border-border-subtle">
        <div className="flex gap-1">
          {canManage && (
            <TabButton active={tab === 'bot'} onClick={() => setTab('bot')}>
              Бот
            </TabButton>
          )}
          {canChats && (
            <TabButton active={tab === 'chats'} onClick={() => setTab('chats')}>
              Чаты
            </TabButton>
          )}
          {canManage && (
            <TabButton active={tab === 'rules'} onClick={() => setTab('rules')}>
              Правила
            </TabButton>
          )}
          {canManage && (
            <TabButton active={tab === 'messages'} onClick={() => setTab('messages')}>
              Сообщения
            </TabButton>
          )}
        </div>
      </div>

      {canManage && tab === 'bot' && <BotTab />}
      {canChats && tab === 'chats' && <ChatsTab />}
      {canManage && tab === 'rules' && <RulesTab />}
      {canManage && tab === 'messages' && <MessagesTab />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`-mb-px px-4 py-2 text-sm font-medium ${
        active
          ? 'border-b-2 border-accent text-accent'
          : 'border-b-2 border-transparent text-text2 hover:text-text'
      }`}
    >
      {children}
    </button>
  );
}

// ─── Chats Tab ──────────────────────────────────────────────────────────────

function ChatsTab() {
  const { data: chats, isLoading } = useTelegramChats();
  const { data: accounts } = useAccounts('all');
  const upsert = useUpsertTelegramChat();
  const remove = useDeleteTelegramChat();
  const [adding, setAdding] = useState(false);
  const [newChatId, setNewChatId] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newLinkedAccount, setNewLinkedAccount] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function add() {
    setError(null);
    if (!/^-?\d+$/.test(newChatId)) {
      setError('chatId должен быть числом (например -1001234567890)');
      return;
    }
    try {
      await upsert.mutateAsync({
        chatId: newChatId,
        chatTitle: newTitle.trim() || null,
        kind: 'group',
        enabled: true,
        linkedAccount: newLinkedAccount || null,
        notes: null,
      });
      setNewChatId('');
      setNewTitle('');
      setNewLinkedAccount('');
      setAdding(false);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-text2">
          Зарегистрированные чаты, в которых бот будет слушать сообщения.
        </div>
        <button onClick={() => setAdding((v) => !v)} className={btnPrimary}>
          {adding ? 'Отмена' : '+ Добавить чат'}
        </button>
      </div>

      {adding && (
        <div className="rounded-md border border-border bg-bg2 p-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
            <input
              placeholder="chatId (число)"
              value={newChatId}
              onChange={(e) => setNewChatId(e.target.value)}
              className={`${inputCls} font-mono`}
            />
            <input
              placeholder="Название (необязательно)"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className={inputCls}
            />
            <select
              value={newLinkedAccount}
              onChange={(e) => setNewLinkedAccount(e.target.value)}
              className={inputCls}
            >
              <option value="">— не привязан к account —</option>
              {(accounts ?? []).map((a) => (
                <option key={a.code} value={a.code}>
                  {a.code} {a.displayName ? `(${a.displayName})` : ''}
                </option>
              ))}
            </select>
            <button onClick={() => void add()} className={btnPrimarySm} disabled={upsert.isPending}>
              {upsert.isPending ? '…' : 'Добавить'}
            </button>
          </div>
          {error && <div className="mt-2 text-xs text-danger">{error}</div>}
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-text2">Загрузка…</div>
      ) : (
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>Chat ID</th>
                <th className={tableHeadCell}>Название</th>
                <th className={tableHeadCell}>Привязан к</th>
                <th className={tableHeadCell}>Статус</th>
                <th className={`${tableHeadCell} text-right`}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {(chats ?? []).length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-12 text-center text-sm text-text2">
                    Чаты не зарегистрированы. Добавь chat_id той группы, куда добавлен бот.
                  </td>
                </tr>
              )}
              {(chats ?? []).map((c) => (
                <ChatRow key={c.chatId} chat={c} onDelete={() => void remove.mutateAsync(c.chatId)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ChatRow({ chat, onDelete }: { chat: TelegramListeningChat; onDelete: () => void }) {
  const update = useUpdateTelegramChat();
  const [showBalances, setShowBalances] = useState(false);
  return (
    <>
      <tr className={tableRow}>
        <td className={`${tableCell} font-mono text-xs`}>{chat.chatId}</td>
        <td className={tableCell}>{chat.chatTitle ?? <span className="text-text-subtle">—</span>}</td>
        <td className={tableCell}>
          {chat.linkedAccount ? (
            <span className="font-mono text-xs text-accent">{chat.linkedAccount}</span>
          ) : (
            <span className="text-text-subtle">—</span>
          )}
        </td>
        <td className={tableCell}>
          <label className="inline-flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              checked={chat.enabled}
              onChange={(e) =>
                void update.mutateAsync({
                  chatId: chat.chatId,
                  patch: { enabled: e.target.checked },
                })
              }
            />
            <span>{chat.enabled ? 'Слушаем' : 'Выключен'}</span>
          </label>
        </td>
        <td className={`${tableCell} text-right`}>
          <button
            onClick={() => setShowBalances((v) => !v)}
            className={btnGhostSm}
            title="Балансы чата, распарсенные из сообщений"
          >
            {showBalances ? '▾ Балансы' : '▸ Балансы'}
          </button>
          <button onClick={onDelete} className={`${btnDanger} ml-1`}>
            Удалить
          </button>
        </td>
      </tr>
      {showBalances && <ChatBalancesRow chatId={chat.chatId} />}
    </>
  );
}

/**
 * Развёрнутая панель балансов чата. Данные создаёт действие «update-chat-balance»
 * в правилах листенера. Запрос выполняется только пока панель смонтирована
 * (авто-обновление каждые 30 сек внутри хука).
 */
function ChatBalancesRow({ chatId }: { chatId: string }) {
  const { data: balances, isLoading } = useTelegramChatBalances(chatId);
  return (
    <tr className={tableRow}>
      <td colSpan={5} className="bg-bg px-4 py-3">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text2">
          Балансы (из сообщений)
        </div>
        {isLoading ? (
          <div className="text-xs text-text2">Загрузка…</div>
        ) : !balances || balances.length === 0 ? (
          <div className="text-xs text-text-subtle">
            Балансов пока нет — их создаёт действие «Обновить баланс чата» в правилах.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {balances.map((b) => (
              <div
                key={b.ccy}
                className="flex items-baseline gap-2 rounded-md border border-border bg-bg2 px-3 py-1.5"
              >
                <span className="rounded border border-border bg-bg3 px-1.5 py-0.5 font-mono text-[10px] font-medium text-text2">
                  {b.ccy}
                </span>
                <span className="font-mono text-sm tabular-nums text-text">
                  {fmtAmt(b.amount, b.ccy as CcyCode)}
                </span>
                <span className="text-[10px] text-text-subtle">
                  обновлено {formatDateTime(b.updatedAt)}
                </span>
              </div>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── Rules Tab ──────────────────────────────────────────────────────────────

function RulesTab() {
  const { data: rules, isLoading } = useTelegramRules();
  const { data: handlers } = useTelegramHandlers();
  const remove = useDeleteTelegramRule();
  const [editing, setEditing] = useState<TelegramRule | null | 'new'>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-text2">
          Регулярные выражения с привязкой к чатам и действием при совпадении.
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPickerOpen(true)} className={btnGhostSm}>
            📚 Из библиотеки
          </button>
          <button onClick={() => setEditing('new')} className={btnPrimary}>
            + Создать правило
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-text2">Загрузка…</div>
      ) : (
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>Название</th>
                <th className={tableHeadCell}>Действие</th>
                <th className={tableHeadCell}>Pattern</th>
                <th className={tableHeadCell}>Чаты</th>
                <th className={tableHeadCell}>Приоритет</th>
                <th className={tableHeadCell}>Статус</th>
                <th className={`${tableHeadCell} text-right`}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {(rules ?? []).length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-12 text-center text-sm text-text2">
                    Правил пока нет.
                  </td>
                </tr>
              )}
              {(rules ?? []).map((r) => {
                const handler = handlers?.find((h) => h.type === r.actionType);
                return (
                  <tr key={r.id} className={tableRow}>
                    <td className={tableCell}>
                      <div className="font-medium text-text">{r.name}</div>
                      {r.description && <div className="text-xs text-text-subtle">{r.description}</div>}
                    </td>
                    <td className={tableCell}>
                      <span className="rounded border border-[var(--accent-border)] bg-[var(--accent-bg)] px-1.5 py-0.5 text-[11px] font-mono text-accent">
                        {handler?.name ?? r.actionType}
                      </span>
                    </td>
                    <td className={`${tableCell} max-w-xs overflow-hidden text-ellipsis font-mono text-xs`} title={r.pattern}>
                      <span>{r.pattern}</span>
                      <span className="ml-1 text-text-subtle">/{r.flags}</span>
                    </td>
                    <td className={`${tableCell} text-xs text-text2`}>
                      {r.chatIds.length === 0 ? '— нет —' : `${r.chatIds.length} шт.`}
                    </td>
                    <td className={`${tableCell} font-mono text-xs`}>{r.priority}</td>
                    <td className={tableCell}>
                      {r.enabled ? (
                        <span className="text-success">●</span>
                      ) : (
                        <span className="text-text-subtle">○</span>
                      )}
                    </td>
                    <td className={`${tableCell} text-right`}>
                      <button onClick={() => setEditing(r)} className={btnGhostSm}>
                        ✎ Изменить
                      </button>
                      <button onClick={() => void remove.mutateAsync(r.id)} className={`${btnDanger} ml-1`}>
                        Удалить
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <TelegramRuleEditorModal
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {pickerOpen && <TelegramTemplatePicker onClose={() => setPickerOpen(false)} />}
    </div>
  );
}

// ─── Messages Tab (message_log — диагностика: что дошло и сматчилось ли) ────

function MessagesTab() {
  const { data: chats, isLoading: chatsLoading } = useTelegramChats();
  const [chatId, setChatId] = useState('');

  // По умолчанию — первый зарегистрированный чат.
  useEffect(() => {
    if (!chatId && chats && chats.length > 0) setChatId(chats[0]!.chatId);
  }, [chats, chatId]);

  // Живой лог: автообновление каждые 10 сек, пока вкладка открыта.
  const { data: messages, isLoading } = useTelegramMessages(chatId || null, 100, {
    refetchInterval: 10_000,
  });

  const options = (chats ?? []).map((c) => ({
    value: c.chatId,
    label: c.chatTitle ?? c.chatId,
    hint: `${c.chatId}${c.enabled ? '' : ' · выключен'}`,
  }));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-text2">
          Лог входящих сообщений — видно, что реально дошло до бота и сматчилось ли с правилами.
        </div>
        <div className="w-72">
          <Combobox
            value={chatId}
            onChange={setChatId}
            options={options}
            placeholder="— выберите чат —"
          />
        </div>
      </div>

      {chatsLoading ? (
        <div className="text-sm text-text2">Загрузка…</div>
      ) : !chats || chats.length === 0 ? (
        <div className="rounded-lg border border-border bg-bg2 px-3 py-12 text-center text-sm text-text2">
          Чаты не зарегистрированы — добавьте чат на вкладке «Чаты», чтобы бот начал слушать
          сообщения.
        </div>
      ) : !chatId ? (
        <div className="rounded-lg border border-border bg-bg2 px-3 py-12 text-center text-sm text-text2">
          Выберите чат, чтобы посмотреть его сообщения.
        </div>
      ) : isLoading ? (
        <div className="text-sm text-text2">Загрузка сообщений…</div>
      ) : (
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>Время</th>
                <th className={tableHeadCell}>Отправитель</th>
                <th className={tableHeadCell}>Текст</th>
                <th className={tableHeadCell}>Совпадения</th>
              </tr>
            </thead>
            <tbody>
              {(messages ?? []).length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-12 text-center text-sm text-text2">
                    Сообщений пока нет — проверьте, что бот запущен (вкладка «Бот»), добавлен в
                    группу и чат включён.
                  </td>
                </tr>
              )}
              {(messages ?? []).map((m) => (
                <MessageRow key={m.id} msg={m} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MessageRow({ msg }: { msg: TelegramMessageLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const text = msg.text ?? '';
  // Разворачивание нужно, если текст длинный или многострочный.
  const expandable = text.length > 140 || text.includes('\n');

  return (
    <tr className={tableRow}>
      <td className={`${tableCell} whitespace-nowrap align-top font-mono text-xs text-text2`}>
        {formatDateTime(msg.receivedAt)}
      </td>
      <td className={`${tableCell} whitespace-nowrap align-top`}>
        <span className="font-mono text-xs">
          {msg.fromUserId ?? <span className="text-text-subtle">—</span>}
        </span>
        {msg.fromIsBot && (
          <span className="ml-1.5 rounded border border-[var(--accent-border)] bg-[var(--accent-bg)] px-1 py-0.5 text-[10px] font-medium text-accent">
            бот
          </span>
        )}
      </td>
      <td className={`${tableCell} max-w-lg align-top`}>
        {text ? (
          <div
            onClick={() => expandable && setExpanded((v) => !v)}
            className={expandable ? 'cursor-pointer' : undefined}
            title={expandable && !expanded ? 'Показать целиком' : undefined}
          >
            {expanded ? (
              <div className="whitespace-pre-wrap break-words font-mono text-xs">{text}</div>
            ) : (
              <div className="truncate font-mono text-xs">{text.split('\n')[0]}</div>
            )}
            {expandable && (
              <button type="button" className="mt-0.5 text-[11px] text-accent hover:underline">
                {expanded ? 'свернуть' : 'развернуть'}
              </button>
            )}
          </div>
        ) : (
          <span className="text-xs text-text-subtle">— без текста —</span>
        )}
      </td>
      <td className={`${tableCell} whitespace-nowrap align-top`}>
        {msg.matchedRules > 0 ? (
          <span className="rounded border border-[var(--green-border)] bg-[var(--green-bg)] px-1.5 py-0.5 text-[11px] font-medium text-success">
            {msg.matchedRules} соб.
          </span>
        ) : (
          <span className="rounded border border-border bg-bg3 px-1.5 py-0.5 text-[11px] text-text2">
            нет совпадений
          </span>
        )}
        {msg.editCount > 0 && (
          <span
            title={msg.editDate ? `изменено ${formatDateTime(msg.editDate)}` : 'изменено'}
            className="ml-1.5 rounded border border-[var(--yellow-border)] bg-[var(--yellow-bg)] px-1.5 py-0.5 text-[11px] font-medium text-warning"
          >
            ред.{msg.editCount > 1 ? ` ×${msg.editCount}` : ''}
          </span>
        )}
      </td>
    </tr>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Bot Tab — статус бота + инструкция по настройке
// ════════════════════════════════════════════════════════════════════════════

function BotTab() {
  const { data: status, isLoading } = useTelegramBotStatus();
  const { data: config } = useTelegramBotConfig();

  // Конфиг сохранён позже, чем процесс API его прочитал → изменения ещё не применены.
  const restartNeeded =
    !!config?.updatedAt &&
    !!status?.listenerStartedAt &&
    new Date(config.updatedAt).getTime() > new Date(status.listenerStartedAt).getTime();

  return (
    <div className="space-y-4">
      {restartNeeded && (
        <div className="flex items-start gap-2 rounded-md border border-[var(--yellow-border)] bg-[var(--yellow-bg)] px-3 py-2.5 text-sm text-warning">
          <span aria-hidden>⚠</span>
          <div>
            <div className="font-medium">
              Конфигурация изменена после запуска API — изменения вступят в силу после рестарта процесса.
            </div>
            <div className="mt-0.5 text-[12px] opacity-80">
              Конфиг сохранён {formatDateTime(config!.updatedAt!)}, процесс прочитал его{' '}
              {formatDateTime(status!.listenerStartedAt!)}.
            </div>
          </div>
        </div>
      )}

      {/* Editable bot configuration */}
      <BotConfigEditor />

      {/* Status card */}
      <div className={cardL1}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className={subSectionTitle}>Статус бота</h3>
          {isLoading ? (
            <span className="text-xs text-text-subtle">проверяем…</span>
          ) : status?.connected ? (
            <span className="inline-flex items-center gap-1 rounded-md border border-[var(--green-border)] bg-[var(--green-bg)] px-2 py-0.5 text-xs font-medium text-success">
              ● Подключён
            </span>
          ) : status?.enabled ? (
            <span className="inline-flex items-center gap-1 rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-2 py-0.5 text-xs font-medium text-danger">
              ● Ошибка подключения
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-md border border-border bg-bg3 px-2 py-0.5 text-xs font-medium text-text2">
              ○ Выключен
            </span>
          )}
        </div>

        {status && (
          <div className="space-y-1.5 text-sm">
            <Row label="Username">
              {status.botUsername ? (
                <a
                  href={`https://t.me/${status.botUsername}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono text-accent hover:underline"
                >
                  @{status.botUsername}
                </a>
              ) : (
                <span className="text-text-subtle">—</span>
              )}
            </Row>
            <Row label="Bot ID">
              <span className="font-mono">
                {status.botId ?? <span className="text-text-subtle">—</span>}
              </span>
            </Row>
            <Row label="Режим работы">
              <span className="font-mono">{status.mode}</span>
              {status.mode === 'polling' && (
                <span className="ml-2 text-[11px] text-text-subtle">
                  long-polling без публичного URL
                </span>
              )}
              {status.mode === 'webhook' && (
                <span className="ml-2 text-[11px] text-text-subtle">
                  Telegram шлёт POST → /tg/webhook
                </span>
              )}
            </Row>
            <Row label="Листенер (процесс)">
              {status.listenerRunning ? (
                <span className="text-success">
                  работает{status.listenerMode ? ` (${status.listenerMode})` : ''}
                  {status.listenerStartedAt && (
                    <span className="ml-1 text-text2">с {formatDateTime(status.listenerStartedAt)}</span>
                  )}
                </span>
              ) : (
                <span className={status.enabled ? 'text-warning' : 'text-text2'}>
                  не запущен в этом процессе
                  {status.listenerStartedAt && (
                    <span className="ml-1 text-text-subtle">
                      (конфиг прочитан {formatDateTime(status.listenerStartedAt)})
                    </span>
                  )}
                  {status.enabled && ' — потребуется рестарт API'}
                </span>
              )}
            </Row>
            <Row label="Токен бота">
              {status.tokenSet ? (
                <span className="text-success">настроен</span>
              ) : (
                <span className="text-danger">не настроен — задай через форму ниже</span>
              )}
            </Row>
            {status.mode === 'webhook' && (
              <>
                <Row label="Webhook URL">
                  <span className="font-mono text-xs">
                    {status.webhookUrl ?? <span className="text-text-subtle">— не задан —</span>}
                  </span>
                </Row>
                <Row label="Webhook secret">
                  {status.webhookSecretSet ? (
                    <span className="text-success">установлен</span>
                  ) : (
                    <span className="text-danger">не задан</span>
                  )}
                </Row>
              </>
            )}
            {status.error && (
              <div className="mt-3 rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
                {status.error}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Setup instructions */}
      <div className={cardL1}>
        <h3 className={`${subSectionTitle} mb-3`}>Как настроить бота</h3>
        <ol className="space-y-3 text-sm text-text">
          <Step n={1} title="Создать бота в Telegram">
            Открой{' '}
            <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-accent hover:underline">
              @BotFather
            </a>
            , выполни <code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-xs">/newbot</code>, придумай имя и username. Сохрани полученный токен.
          </Step>
          <Step n={2} title="Включить нужные настройки в @BotFather">
            <ul className="ml-4 mt-1 list-disc space-y-1 text-[13px] text-text2">
              <li>
                <code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-[11px]">/setprivacy</code> →{' '}
                <strong>Disable</strong> — чтобы бот видел все сообщения в группе, а не только адресованные ему.
              </li>
              <li>
                <code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-[11px]">/setjoingroups</code> →{' '}
                <strong>Enable</strong> — чтобы можно было добавлять в группы.
              </li>
              <li>
                <strong>Bot-to-Bot Communication Mode → Enable</strong> — чтобы видеть сообщения от других ботов (банковские, обменные).
              </li>
            </ul>
          </Step>
          <Step n={3} title="Сохранить токен через панель">
            Открой вкладку <strong>«Бот»</strong> выше → вставь токен от BotFather в поле «Токен бота» → выбери режим (<code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-[11px]">polling</code> для dev, <code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-[11px]">webhook</code> для production) → отметь «Listener включён» → нажми «Сохранить».
            <div className="mt-1.5 text-[12px] text-text2">
              Токен шифруется AES-256-GCM перед записью в БД. В <code className="rounded bg-bg3 px-1 py-0.5 font-mono text-[11px]">.env</code> ничего трогать не нужно.
            </div>
          </Step>
          <Step n={4} title="Добавить бота в чаты">
            Добавь <strong>@{status?.botUsername ?? 'YourBot'}</strong> в группы исполнителей. Сделай его <strong>администратором</strong> группы — это нужно чтобы бот видел сообщения других ботов (банковских уведомлений).
          </Step>
          <Step n={5} title="Зарегистрировать чаты в WavePanel">
            Перейди на вкладку <strong>«Чаты»</strong> выше и добавь <code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-xs">chat_id</code> каждой группы. ID можно узнать через @userinfobot, @getidsbot или из логов API после первого сообщения.
          </Step>
          <Step n={6} title="Настроить правила">
            На вкладке <strong>«Правила»</strong> создай regex-правила для каждого типа уведомления. Используй кнопку «📚 Из библиотеки» для готовых шаблонов Тинькофф/Сбер/Binance/курс-ботов.
          </Step>
        </ol>
      </div>

      {/* Tips */}
      <div className={cardL1}>
        <h3 className={`${subSectionTitle} mb-2`}>Подсказки</h3>
        <ul className="space-y-1.5 text-sm text-text2">
          <li>
            <strong>Polling vs Webhook:</strong> для dev/локальной разработки используй{' '}
            <code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-[11px]">polling</code> — не нужен публичный URL.
            В production лучше <code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-[11px]">webhook</code> — Telegram шлёт обновления сам, меньше нагрузка.
          </li>
          <li>
            <strong>Один токен — один экземпляр.</strong> Бот может быть подключён только к одному процессу одновременно. В polling-режиме защита через pg_advisory_lock.
          </li>
          <li>
            <strong>Безопасность токена:</strong> токен хранится в БД зашифрованным (AES-256-GCM). В API и UI открытый вид не отдаётся — только маска вида <code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-[11px]">7000••••token</code>. Если токен утёк — перевыпусти через @BotFather (<code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-[11px]">/revoke</code>) и сохрани новый через форму выше.
          </li>
        </ul>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="w-44 text-[11px] uppercase tracking-wide text-text2">{label}</span>
      <span className="flex-1">{children}</span>
    </div>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', { hour12: false });
}

/**
 * Редактор конфигурации бота: токен (password+маска), режим, webhook params,
 * флаг enabled, кнопка проверки подключения. Токен передаётся на бэк только
 * при явном вводе; в БД хранится зашифрованно (AES-256-GCM).
 */
function BotConfigEditor() {
  const { data: config, isLoading } = useTelegramBotConfig();
  const save = useSaveBotConfig();
  const test = useTestBotToken();

  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState<'polling' | 'webhook'>('polling');
  const [tokenInput, setTokenInput] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [pollingTimeoutS, setPollingTimeoutS] = useState(25);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; botUsername: string | null; error: string | null } | null>(
    null,
  );

  // Подгружаем актуальные значения при первой загрузке config.
  useEffect(() => {
    if (!config) return;
    setEnabled(config.enabled);
    setMode(config.mode);
    setWebhookUrl(config.webhookUrl ?? '');
    setPollingTimeoutS(config.pollingTimeoutS);
  }, [config]);

  async function handleSave() {
    setSaveError(null);
    setTestResult(null);
    try {
      await save.mutateAsync({
        enabled,
        mode,
        // token передаём только если оператор реально ввёл новое значение
        ...(tokenInput.trim() ? { token: tokenInput.trim() } : {}),
        ...(mode === 'webhook' && webhookUrl.trim() ? { webhookUrl: webhookUrl.trim() } : {}),
        ...(mode === 'webhook' && webhookSecret.trim() ? { webhookSecret: webhookSecret.trim() } : {}),
        pollingTimeoutS,
      });
      setTokenInput('');
      setWebhookSecret('');
    } catch (err) {
      setSaveError((err as Error).message);
    }
  }

  async function handleTest() {
    setTestResult(null);
    setSaveError(null);
    try {
      const result = await test.mutateAsync({
        ...(tokenInput.trim() ? { token: tokenInput.trim() } : {}),
      });
      setTestResult(result);
    } catch (err) {
      setTestResult({ ok: false, botUsername: null, error: (err as Error).message });
    }
  }

  async function handleClearToken() {
    setSaveError(null);
    if (!confirm('Удалить токен бота? Listener остановится после рестарта API.')) return;
    try {
      await save.mutateAsync({ token: null, enabled: false });
    } catch (err) {
      setSaveError((err as Error).message);
    }
  }

  if (isLoading) return <div className={cardL1}>Загрузка конфигурации…</div>;

  return (
    <div className={cardL1}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className={subSectionTitle}>Настройки бота</h3>
        {config?.source === 'env' && (
          <span className="text-[10px] text-text-subtle">
            сейчас читается из env — заполни форму чтобы перенести в БД
          </span>
        )}
      </div>

      <div className="space-y-3">
        {/* Enable toggle */}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span className="font-medium text-text">Listener включён</span>
          <span className="text-[11px] text-text-subtle">
            (когда выключен — бот не подключается к Telegram, чаты не слушаются)
          </span>
        </label>

        {/* Token */}
        <div>
          <label className={fieldLabelCls}>Токен бота (от @BotFather)</label>
          <div className="flex items-stretch gap-2">
            <input
              type={showToken ? 'text' : 'password'}
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder={
                config?.tokenSet
                  ? `Сохранён: ${config.tokenMask ?? '••••'} (оставь пустым, чтобы не менять)`
                  : '7000000000:AAH... (получи через /newbot)'
              }
              className={`${inputCls} font-mono text-xs`}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowToken((v) => !v)}
              className={btnGhostSm}
              title={showToken ? 'Скрыть' : 'Показать'}
            >
              {showToken ? '🙈' : '👁'}
            </button>
            {config?.tokenSet && (
              <button
                type="button"
                onClick={() => void handleClearToken()}
                className={btnDanger}
                title="Удалить токен"
              >
                ✕
              </button>
            )}
          </div>
          <div className="mt-1 text-[10px] text-text-subtle">
            Токен шифруется AES-256-GCM перед записью в БД. Ключ шифрования создаётся автоматически при первом запуске API (файл <code className="rounded bg-bg3 px-1 py-0.5 font-mono text-[10px]">apps/api/.data/secrets.key</code>, права 0600). Через API/UI открытый токен наружу не отдаётся — только маска.
          </div>
        </div>

        {/* Mode + polling timeout */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
          <div>
            <label className={fieldLabelCls}>Режим работы</label>
            <div className="flex gap-1">
              {(['polling', 'webhook'] as const).map((m) => (
                <label
                  key={m}
                  className={`cursor-pointer rounded-md border px-3 py-1.5 text-xs ${
                    mode === m
                      ? 'border-[var(--accent-border)] bg-[var(--accent-bg)] text-accent'
                      : 'border-border bg-bg text-text2 hover:text-text'
                  }`}
                >
                  <input
                    type="radio"
                    className="sr-only"
                    checked={mode === m}
                    onChange={() => setMode(m)}
                  />
                  {m === 'polling' ? 'Long-polling (dev)' : 'Webhook (prod)'}
                </label>
              ))}
            </div>
          </div>
          {mode === 'polling' && (
            <div>
              <label className={fieldLabelCls}>Polling timeout, сек</label>
              <input
                type="number"
                value={pollingTimeoutS}
                onChange={(e) => setPollingTimeoutS(Number(e.target.value))}
                min={1}
                max={50}
                className={`${inputCls} h-9 w-24 text-xs`}
              />
            </div>
          )}
        </div>

        {/* Webhook params */}
        {mode === 'webhook' && (
          <div className="grid grid-cols-1 gap-3 rounded-md border border-border bg-bg2 p-3 md:grid-cols-2">
            <div>
              <label className={fieldLabelCls}>Webhook URL (https)</label>
              <input
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://api.example.com/tg/webhook"
                className={`${inputCls} font-mono text-xs`}
              />
            </div>
            <div>
              <label className={fieldLabelCls}>Webhook secret-token</label>
              <input
                type="password"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder={
                  config?.webhookSecretSet ? 'Сохранён (оставь пустым чтобы не менять)' : '1-256 символов'
                }
                className={`${inputCls} font-mono text-xs`}
                autoComplete="new-password"
              />
            </div>
            <div className="text-[10px] text-text-subtle md:col-span-2">
              Telegram передаёт этот secret в заголовке{' '}
              <code className="rounded bg-bg3 px-1 font-mono">X-Telegram-Bot-Api-Secret-Token</code>;
              запросы без него или с другим — отбрасываются.
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex flex-wrap items-center gap-2 border-t border-border-subtle pt-3">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={save.isPending}
            className={btnPrimary}
          >
            {save.isPending ? 'Сохранение…' : 'Сохранить'}
          </button>
          <button
            type="button"
            onClick={() => void handleTest()}
            disabled={test.isPending || (!tokenInput && !config?.tokenSet)}
            className={btnGhostSm}
          >
            {test.isPending ? 'Проверка…' : '🔍 Проверить токен (getMe)'}
          </button>
          <span className="ml-auto text-[10px] text-text-subtle">
            Изменения применяются после рестарта API процесса.
          </span>
        </div>

        {saveError && (
          <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
            {saveError}
          </div>
        )}
        {testResult && (
          <div
            className={`rounded-md border px-3 py-2 text-sm ${
              testResult.ok
                ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-success'
                : 'border-[var(--red-border)] bg-[var(--red-bg)] text-danger'
            }`}
          >
            {testResult.ok ? (
              <>✓ Подключение успешно. Бот: @{testResult.botUsername}</>
            ) : (
              <>✗ Ошибка: {testResult.error}</>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--accent-bg)] text-xs font-bold text-accent">
        {n}
      </span>
      <div className="flex-1">
        <div className="font-medium text-text">{title}</div>
        <div className="mt-0.5 text-[13px] text-text2">{children}</div>
      </div>
    </li>
  );
}
